// ═══════════════════════════════════════════════════════════════════
//  THE BOOKING SEASON, AS THE CALENDAR READS IT
//
//  When the public may book: from `opensAt` up to, not including,
//  `closesAt`. Staff are never held to it — the calendar only says which
//  times fall outside it, so whoever is booking at the counter knows the
//  household could not have booked that time themselves.
//
//  Not the same thing as `outsideBookingWindow`, Cal's 28-day lookahead.
//  That one is about how far ahead a slot is; this one is about whether
//  the season covers it at all.
// ═══════════════════════════════════════════════════════════════════

import type { BookingSeasonSnapshot } from "@/app/appointments/booking-season/bookingSeasonApi"
import { closedReasonLabel, formatSeasonDateTime } from "@/app/appointments/booking-season/bookingSeasonFormat"

/**
 * The parts of the season the grid needs. A `BookingSeasonSnapshot` is one; taking only these keeps
 * the grid's input plain and its tests short.
 */
export type BookingSeasonBounds = Pick<BookingSeasonSnapshot, "opensAt" | "closesAt" | "closedReason">

/** What the legend, a cell's hint and the dialog all call it. */
export const OUTSIDE_BOOKING_SEASON_LABEL = "Outside booking season (public can't book)"

/**
 * Whether the public could not book an appointment starting at `start`.
 *
 * Undefined `season` is "not known" — still loading, or the read failed — and answers false: the
 * calendar then says nothing about the season rather than marking every slot on a guess. A season
 * that was never saved is a different answer, and a definite one: the public can book nothing.
 *
 * Compared as instants, start inclusive and close exclusive, matching the server's own
 * `opensAt <= now < closesAt`.
 */
export function isOutsideBookingSeason(start: Date, season: BookingSeasonBounds | undefined): boolean {
  if (!season) return false
  if (season.closedReason === "NotConfigured") return true

  // Bounds missing without the reason saying so should not happen. Read as no season at all, the
  // same as NotConfigured, rather than as a season with no edges.
  if (!season.opensAt || !season.closesAt) return true

  const at = start.getTime()
  return at < Date.parse(season.opensAt) || at >= Date.parse(season.closesAt)
}

/**
 * The line above the grid: whether the public can book right now, and — for staff — why not.
 * Null while the season is not known, so the banner draws nothing rather than a guess.
 */
export function bookingSeasonBannerText(season: BookingSeasonSnapshot | undefined): string | null {
  if (!season) return null

  if (season.status === "Open") {
    return season.closesAt
      ? `Public booking open until ${formatSeasonDateTime(season.closesAt)}`
      : "Public booking open"
  }

  return `Public booking closed: ${closedReasonLabel(season) ?? "No season set"}`
}
